// client/src/components/verification/VerifySecondaryEmailPage.js
// Import useRef
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmSecondaryEmailVerification } from '../../api/authApi';
import LoadingSpinner from '../common/LoadingSpinner';
import './VerifyPage.css'; // Ensure styles are imported

function VerifySecondaryEmailPage() {
    const { t } = useTranslation();
    const { token } = useParams();
    const navigate = useNavigate();
    const [verificationStatus, setVerificationStatus] = useState('verifying');
    const [message, setMessage] = useState('');
    // Ref to track if success has occurred during this effect run
    const successAchievedRef = useRef(false);

    const VERIFICATION_TIMEOUT_MS = 20000; // 20 seconds
    const REDIRECT_DELAY_MS = 4000; // 4 seconds delay before redirect

    useEffect(() => {
        let isMounted = true;
        let redirectTimeoutId = null;
        // Reset success ref on each run related to token/t change
        successAchievedRef.current = false;

        const timeoutId = setTimeout(() => {
            if (isMounted && verificationStatus === 'verifying' && !successAchievedRef.current) {
                console.error("[VerifyPage] Verification timed out.");
                setMessage(t('verification.error.timeout', 'Verification took too long. Please try again.'));
                setVerificationStatus('error');
            }
        }, VERIFICATION_TIMEOUT_MS);

        const verifyToken = async () => {
            if (!token) { /* ... handle missing token ... */ clearTimeout(timeoutId); return; }

            if (isMounted && verificationStatus !== 'success' && verificationStatus !== 'error') {
                setVerificationStatus('verifying');
                setMessage(t('verification.verifying', 'Verifying your email...'));
            }

            try {
                console.log("[VerifyPage] Calling confirmSecondaryEmailVerification...");
                const response = await confirmSecondaryEmailVerification(token);
                console.log("[VerifyPage] API call successful:", response);
                if (isMounted) {
                    clearTimeout(timeoutId); // Clear verification timeout
                    setMessage(response.message || t('verification.success.secondaryEmail', 'Secondary email verified successfully!'));
                    setVerificationStatus('success');
                    successAchievedRef.current = true;

                    // --- Add Timed Redirect on Success ---
                    console.log(`[VerifyPage] Scheduling redirect to /login in ${REDIRECT_DELAY_MS}ms`);
                    redirectTimeoutId = setTimeout(() => {
                        console.log("[VerifyPage] Redirecting to /login now.");
                        navigate('/login', { replace: true }); // Redirect to login page
                    }, REDIRECT_DELAY_MS);
                    // --- End Timed Redirect ---
                }
            } catch (error) {
                console.error("[VerifyPage] Caught error...", error);
                if (isMounted) {
                    clearTimeout(timeoutId); // Clear verification timeout
                    if (!successAchievedRef.current) {
                        setMessage(error.message || t('verification.error.generic', '...'));
                        setVerificationStatus('error');
                    } else { console.log("[VerifyPage] Ignoring error after success..."); }
                }
            }
        };

        verifyToken();

        // Cleanup: clear timeouts and set isMounted false
        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            if (redirectTimeoutId) { // Clear redirect timeout if component unmounts
                clearTimeout(redirectTimeoutId);
            }
        };
        // --- Dependency array no longer needs verificationStatus ---
    }, [token, t, navigate, verificationStatus]); // Only re-run if token or t function changes

    return (
        <div className="verify-page">
            <div className="verify-page-container" style={{ textAlign: 'center', padding: '40px' }}>
                <h1>{t('dashboard.account.contact.verification.secondaryEmailTitle', 'Secondary Email Verification')}</h1>
                {verificationStatus === 'verifying' && (
                    <>
                        <LoadingSpinner />
                        {message && <p>{message}</p>}
                    </>
                )}
                {verificationStatus === 'success' && (
                    <div className="verify-success-message">
                        <p>{t('dashboard.account.contact.verification.success', 'Verification successful!')}</p>
                        {/* Keep the link as a fallback */}
                        <p style={{ marginTop: '15px', fontSize: '0.9em' }}>
                            {t('verification.redirecting', 'You will be redirected shortly...')} <Link to="/login">{t('verification.returnToLogin', 'Return to Login')}</Link>
                        </p>
                    </div>
                )}
                {verificationStatus === 'error' && !token && (<div className="error-message">{t('dashboard.account.contact.verification.error', 'Verification failed.')}</div>)}
            </div>
        </div>
    );
}

export default VerifySecondaryEmailPage;